import { AfterViewInit, Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BasicQuestionGroupDto } from '@proxy/question-groups';
import { QuestionDto, QuestionInputDto, QuestionService, QuestionState, QuestionType } from '@proxy/questions';
import { firstValueFrom } from 'rxjs';
import { appModuleAnimation } from 'src/app/shared/animations/routerTransition';
import { AppBaseComponent } from 'src/app/shared/app-base.component';
import { AppPathConsts } from 'src/app/shared/AppPathConsts';
import { QuestionGroupDropdownComponent } from 'src/app/shared/components/question-group-dropdown/question-group-dropdown.component';

@Component({
    selector: 'app-create-update-question',
    templateUrl: './create-update-question.component.html',
    animations: [appModuleAnimation()],
    standalone: false
})
export class CreateUpdateQuestionComponent extends AppBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(QuestionGroupDropdownComponent) questionGroupDropdownComponent: QuestionGroupDropdownComponent;

  @Input() isTemplateFlow = false;
  @Input() isCommonTemplate: boolean;
  @Input() questionGroupTemplate: BasicQuestionGroupDto;
  @Output() onCreateQuestionForTemplate = new EventEmitter<QuestionDto>();
  
  questionId: number;
  title = '';
  isEdition: boolean;

  content: string;
  type: QuestionType;
  state: QuestionState;
  isCommon: boolean;
  questionGroup: BasicQuestionGroupDto;

  constructor(
    injector: Injector,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly _questionService: QuestionService) {
    super(injector);
  }

  ngOnInit(): void {
    this.title = this.l('::CreateQuestion');
    let questionId: number;

    if (this.isTemplateFlow) {
      this.isCommon = this.isCommonTemplate;
      this.questionGroup = this.questionGroupTemplate;
      this.state = QuestionState.Active;
    } else {
      questionId = +this.route.snapshot.paramMap.get('id');
    }

    if (questionId) {
      this.questionId = questionId;
      this.isEdition = true;
      this.title = this.l('::EditQuestion');
      this.getQuestion();
    }

    this.setTitle(this.title);
  }

  ngOnDestroy(): void {
    this.setTitle(this.getDefaultTitle());
  }

  ngAfterViewInit(): void {
    if (!this.isEdition) {
      this.questionGroupDropdownComponent.loadQuestionGroups(this.isCommon);
    }
  }

  goBack(): void {
    this.router.navigate([`${AppPathConsts.ConfigurationPrefix}/${AppPathConsts.Questions}`]);
  }

  async saveQuestion(): Promise<void> {
    try {
      this.showLoading();

      const questionInputDto: QuestionInputDto = {
        id: this.questionId,
        content: this.content,
        type: this.type,
        state: this.state,
        isCommon: this.isCommon,
        questionGroupId: this.isNullOrUndefined(this.questionGroup) ? undefined : this.questionGroup.id,
      };

      let questionDtoCreated: QuestionDto;

      if (this.isEdition) {
        await firstValueFrom(this._questionService.editQuestionByInputDto(questionInputDto));
      } else {
        questionDtoCreated = await firstValueFrom(this._questionService.createQuestionByInputDto(questionInputDto));
      }

      this.notifySuccess('::QuestionSavedSuccessfully', '::SavedSuccessfully');

      if (!this.isTemplateFlow) {
        this.goBack();
        return;
      }

      this.onCreateQuestionForTemplate.emit(questionDtoCreated);

    } finally {
      this.hideLoading();
    }
  }

  onSelectState(state: QuestionState): void {
    this.state = state;
  }

  onSelectType(type: QuestionType): void {
    this.type = type;
  }

  onSelectQuestionGroup(questionGroup: BasicQuestionGroupDto): void {
    this.questionGroup = questionGroup;
  }

  onIsCommonChange(isCommon: boolean): void {
    this.isCommon = isCommon;

    if (!this.isNullOrUndefined(this.questionGroupDropdownComponent)) {
      this.questionGroup = undefined;
      this.questionGroupDropdownComponent.loadQuestionGroups(this.isCommon);
    }
  }

  isValidQuestion(): boolean {
    return !this.isEmptyString(this.content) && !this.isNullOrUndefined(this.type) && !this.isNullOrUndefined(this.state);
  }

  private async getQuestion(): Promise<void> {
    try {
      this.showLoading();

      const questionDto = await firstValueFrom(this._questionService.getQuestionByQuestionId(this.questionId));
      this.content = questionDto.content;
      this.type = questionDto.type;
      this.state = questionDto.state;
      this.isCommon = questionDto.isCommon;
      this.questionGroup = questionDto.questionGroup;

      this.questionGroupDropdownComponent.loadQuestionGroups(this.isCommon);

    } finally {
      this.hideLoading();
    }
  }
}
